import {
  IWithSearchParamsProps,
  withSearchParams,
} from '../../../../shared/lib/hoc/WithSearchParams'
import { ILanguageComplianceFilter } from '../../model/types/LanguageComplianceFilter.interface'
import {
  selectLanguageComplianceFilter,
  setLanguageComplianceFilter,
} from '../../model/languageComplianceSlice'
import { FilterDrop } from '../../../../shared/components/cards/filter-drop/FilterDrop'
import { Controller, useForm } from 'react-hook-form'
import { useAppDispatch, useAppSelector } from '../../../../app/store/hooks/Redux'
import React, { useEffect, useMemo } from 'react'
import { BpSelect } from '../../../../shared/components/forms/select/BpSelect'
import { IOption } from '../../../../shared/components/forms/search-input/BpSearchInput'
import { BpDatePicker } from '../../../../shared/components/forms/datepicker/BpDatePicker'
import { setCanCloseDropdown } from '../../../../app/store/reducers/AppSlice'
import moment from 'moment/moment'
import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_RANGE_FORMAT,
} from '../../../../shared/consts/DefaultRangeFormat.const'
import { Switch } from 'antd'
import {
  LanguageComplianceFilterSearchArg,
  useGetLanguageComplianceFilterAffiliateQuery,
  useGetLanguageComplianceFilterCountryQuery,
  useGetLanguageComplianceFilterPageQuery,
  useGetLanguageComplianceFilterProjectQuery,
  useGetLanguageComplianceFilterWordQuery,
} from '../../../../app/api'
import { mapToOptions } from '../../../../shared/lib/helpers/MapToOptions'
import { FilterChips } from '../../../../shared/components/utility/filter-chips/FilterChips'
import clsx from 'clsx'
import styles from './LanguageComplianceFilters.module.scss'
import { ProjectIcon } from '../../../../shared/components/icons/filters/ProjectIcon'
import { CountryIcon } from '../../../../shared/components/icons/filters/CountryIcon'
import { useSearchState } from '../../../../lib/hooks/useSearchState'
import { AffiliateIcon } from '../../../../shared/components/icons/filters/AffiliateIcon'
import { UrlIcon } from '../../../../shared/components/icons/filters/UrlIcon'
import { toTextOptions } from '../../../../shared/lib/helpers/ToTextOptions'
import { withSelectedOptions } from '../../../../shared/lib/helpers/WithSelectedOptions'
import { useTranslation } from 'react-i18next'
import { restoreAppliedValues } from '../../../../shared/lib/helpers/AppliedValues'
import {
  resetFilterFields,
  useRegisterAppliedFilters,
} from '../../../../lib/context/applied-filters/AppliedFiltersProvider'

/** Фильтры, которые сбрасывает Clear filters, и их значения по умолчанию. */
const CLEARABLE_FILTERS: Partial<ILanguageComplianceFilter> = {
  url: '',
  affiliate: '',
  restrictedWords: '',
  hidden: false,
}

const Filter = ({
  searchParams,
  setSearchParams,
}: IWithSearchParamsProps<ILanguageComplianceFilter>) => {
  const filters = useAppSelector<ILanguageComplianceFilter>(selectLanguageComplianceFilter)
  const { project, country, url, affiliate, dateRange, restrictedWords, hidden } = filters
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [search, setSearch] = useSearchState({ affiliate: '', url: '' })

  const {
    getValues,
    setValue,
    handleSubmit,
    control,
    formState: { defaultValues },
  } = useForm<ILanguageComplianceFilter>({
    mode: 'onChange',
    defaultValues: {
      project,
      country,
      url,
      affiliate,
      dateRange,
      restrictedWords,
      hidden,
      ...searchParams,
    },
  })

  useEffect(() => {
    applyFilter()
  }, [])

  const {
    data: projects,
    isFetching: projectsFetching,
    isLoading: projectsLoading,
  } = useGetLanguageComplianceFilterProjectQuery()
  const projectsOptions = useMemo(() => mapToOptions(projects), [projects])

  const {
    data: countries,
    isFetching: countriesFetching,
    isLoading: countriesLoading,
  } = useGetLanguageComplianceFilterCountryQuery({ projectId: Number(project) }, { skip: !project })
  const countriesOptions = useMemo(() => mapToOptions(countries), [countries])

  const {
    data: pages,
    isFetching: pagesFetching,
    isLoading: pagesLoading,
  } = useGetLanguageComplianceFilterPageQuery(
    { projectId: Number(project), search: search.url } as LanguageComplianceFilterSearchArg,
    { skip: !project },
  )
  const pagesOptions = useMemo(() => mapToOptions(pages), [pages])

  const {
    data: affiliates,
    isFetching: affiliatesFetching,
    isLoading: affiliatesLoading,
  } = useGetLanguageComplianceFilterAffiliateQuery(
    { projectId: Number(project), search: search.affiliate } as LanguageComplianceFilterSearchArg,
    { skip: !project },
  )
  const affiliatesOptions = useMemo(() => mapToOptions(affiliates), [affiliates])

  const {
    data: restrictedWords_,
    isFetching: restrictedWordsFetching,
    isLoading: restrictedWordsLoading,
  } = useGetLanguageComplianceFilterWordQuery({ projectId: Number(project) }, { skip: !project })
  const restrictedWordsOptions = useMemo(() => mapToOptions(restrictedWords_), [restrictedWords_])

  const applyFilter = (): void => {
    const values = getValues()
    dispatch(setLanguageComplianceFilter(values))
    if (setSearchParams) setSearchParams(values)
  }

  useRegisterAppliedFilters({
    values: filters,
    defaults: CLEARABLE_FILTERS,
    onClear: () => {
      resetFilterFields(setValue, CLEARABLE_FILTERS)
      applyFilter()
    },
  })

  useEffect(() => {
    if (!country && countriesOptions?.length) {
      setValue('country', countriesOptions[0].value)
      applyFilter()
    }
  }, [country, countriesOptions])

  return (
    <>
      <form className={clsx(styles.form)} onSubmit={handleSubmit(applyFilter)}>
        <div className={clsx(styles.field)}>
          <Controller
            render={({ field: { onChange, ...fieldProps } }) => {
              const defaultValue = defaultValues?.project
                ? defaultValues.project
                : project
                  ? project
                  : projectsOptions?.length && projectsOptions[0].value

              return (
                <BpSelect
                  {...fieldProps}
                  Icon={ProjectIcon}
                  defaultValue={defaultValue}
                  disabled={projectsLoading || projectsFetching}
                  fieldSize='lg'
                  loading={projectsLoading || projectsFetching}
                  options={projectsOptions}
                  title='Project'
                  onChange={(e) => {
                    const previousProject = getValues('project')

                    onChange(e)

                    if (previousProject && e.target.value !== previousProject) {
                      setValue('country', '')
                      setValue('url', '')
                      setValue('affiliate', '')
                      setValue('restrictedWords', '')
                    }

                    applyFilter()
                  }}
                ></BpSelect>
              )
            }}
            control={control}
            name='project'
          />
        </div>
        <div className={clsx(styles.field)}>
          <Controller
            render={({ field: { value, onChange, ...fieldProps } }) => {
              return (
                <BpSelect
                  {...fieldProps}
                  multiple
                  Icon={CountryIcon}
                  disabled={countriesLoading || countriesFetching}
                  fieldSize='lg'
                  loading={countriesLoading || countriesFetching}
                  options={countriesOptions}
                  title='Country'
                  value={value?.split(',')}
                  onChange={(e) => {
                    onChange(
                      [].slice
                        .call(e.target.selectedOptions)
                        .map((o: IOption) => o.value)
                        .join(','),
                    )
                  }}
                  onDropClose={applyFilter}
                ></BpSelect>
              )
            }}
            control={control}
            name='country'
          />
        </div>
        <div className={clsx(styles.field)}>
          <Controller
            render={({ field: { value, onChange, ...fieldProps } }) => (
              <BpSelect
                {...fieldProps}
                multiple
                Icon={UrlIcon}
                enableChipLoad={false}
                fieldSize='lg'
                loading={pagesLoading || pagesFetching}
                options={withSelectedOptions('page', pagesOptions, value)}
                placeholder='Page'
                value={value?.split(',')}
                onChange={(e) => {
                  onChange(
                    [].slice
                      .call(e.target.selectedOptions)
                      .map((o: IOption) => o.value)
                      .join(','),
                  )
                }}
                onDropClose={applyFilter}
                onSearch={setSearch('url')}
              ></BpSelect>
            )}
            control={control}
            name='url'
          />
        </div>
        <div className={clsx(styles.field)}>
          <Controller
            render={({ field: { value, onChange, ...fieldProps } }) => (
              <BpSelect
                {...fieldProps}
                multiple
                Icon={AffiliateIcon}
                enableChipLoad={false}
                fieldSize='lg'
                loading={affiliatesLoading || affiliatesFetching}
                options={toTextOptions(affiliatesOptions, value)}
                placeholder='Affiliate ID'
                value={value?.split(',')}
                onChange={(e) => {
                  onChange(
                    [].slice
                      .call(e.target.selectedOptions)
                      .map((o: IOption) => o.value)
                      .join(','),
                  )
                }}
                onDropClose={applyFilter}
                onSearch={setSearch('affiliate')}
              ></BpSelect>
            )}
            control={control}
            name='affiliate'
          />
        </div>
        <div className={clsx(styles.filterDropWrapper)}>
          <FilterDrop
            values={getValues(['dateRange', 'restrictedWords', 'hidden'])}
            onClear={() => {
              setValue('restrictedWords', '')
              setValue('hidden', false)
            }}
            onHide={() => restoreAppliedValues(getValues, setValue, filters)}
            onSubmit={applyFilter}
          >
            <Controller
              render={({ field: { onChange, value } }) => {
                return (
                  <BpDatePicker
                    defaultValue={[
                      moment(value, DEFAULT_DATE_FORMAT),
                      moment(value, DEFAULT_RANGE_FORMAT),
                    ]}
                    label='Date'
                    onChange={(e, t) => onChange(`${t[0]} - ${t[1]}`)}
                    onOpenChange={(e) => dispatch(setCanCloseDropdown(!e))}
                  />
                )
              }}
              control={control}
              name='dateRange'
            />
            <Controller
              render={({ field: { value, onChange, ...fieldProps } }) => (
                <BpSelect
                  placeholder={t('fields.other.selectFromList')}
                  {...fieldProps}
                  multiple
                  disabled={restrictedWordsLoading || restrictedWordsFetching}
                  loading={restrictedWordsLoading || restrictedWordsFetching}
                  options={restrictedWordsOptions}
                  title='Restricted words'
                  value={value?.split(',')}
                  onChange={(e) => {
                    onChange(
                      [].slice
                        .call(e.target.selectedOptions)
                        .map((o: IOption) => o.value)
                        .join(','),
                    )
                  }}
                ></BpSelect>
              )}
              control={control}
              name='restrictedWords'
            />
            <Controller
              render={({ field }) => (
                <div className={clsx(styles.switchRow)}>
                  <span className={clsx(styles.switchLabel)}>Show hidden pages</span>
                  <Switch {...field} checked={field.value} defaultChecked={defaultValues?.hidden} />
                </div>
              )}
              control={control}
              name='hidden'
            />
          </FilterDrop>
        </div>
      </form>
      <div className={clsx(styles.chipsWrapper)}>
        <FilterChips
          clearCallBack={(field: any) => {
            setValue(field, '')
            applyFilter()
          }}
          items={[
            { title: 'Date', field: 'dateRange', value: dateRange, clearable: false },
            {
              title: 'Restricted words',
              field: 'restrictedWords',
              value: restrictedWords,
              data: restrictedWordsOptions,
              type: 'multiselect',
            },
          ]}
          applyCallBack={applyFilter}
        />
      </div>
    </>
  )
}

export const LanguageComplianceFilters = withSearchParams<
  IWithSearchParamsProps<ILanguageComplianceFilter>,
  ILanguageComplianceFilter
>(Filter, selectLanguageComplianceFilter)
